import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { findById } from '../../utils/methods';
import { defaultFilterValue, initialFilterValue } from './accountListConfig';
import useToggle from '../../hooks/useToggle';
import { getFilteredAccounts } from './accountListUtils';

const useAccountList = (props) => {
  const {
    filters, showAdvanceFilter, isNavigatedFromRiskAccount, isNavigatedFromAccountDashboard, accountListMenuId,
  } = props;

  const navigate = useNavigate();
  const [toggleAdvanceFilters, setToggleAdvanceFilters] = useToggle(filters.toggleAdvanceFilter ?? showAdvanceFilter);
  // State to store API data ----->
  const [accountList, setAccountList] = useState({
    data: [],
    error: '',
    loading: true,
  });

  const [featureList, setFeatureList] = useState({
    data: [],
    error: '',
    loading: true,
  });

  const [featureUsageAccounts, setFeatureUsageAccounts] = useState({
    data: [],
    error: '',
    loading: true,
  });

  const accountListData = useSelector((state) => state.accountsList);
  const featureData = useSelector((state) => state.featuresList);
  const featureUsageAccountData = useSelector((state) => state.featureUsageAccounts);
  const customFilterViewList = useSelector((state) => state.customFilterView.data);
  const siteConfiguration = useSelector((state) => state.siteConfiguration.data);

  // States & functions ----->
  const [selectedFilters, setSelectedFilters] = useState({ ...initialFilterValue, ...filters });
  const [openDialog, setOpenDialog] = useToggle();
  const [selectedView, setSelectedView] = useState(accountListMenuId === 'all' ? defaultFilterValue : accountListMenuId);

  const findSelectedView = (id) => {
    return findById(id, customFilterViewList);
  };

  const resetFilters = () => {
    setSelectedFilters(initialFilterValue);
  };

  const filteredAccounts = useMemo(() => {
    return getFilteredAccounts({
      accountList: accountList.data,
      selectedFilters,
      siteConfiguration,
      featureUsageAccounts: featureUsageAccounts.data,
      isNavigatedFromRiskAccount,
    });
  }, [accountList.data, selectedFilters, siteConfiguration, featureUsageAccounts.data, isNavigatedFromRiskAccount]);

  useEffect(() => {
    if (isNavigatedFromAccountDashboard) {
      return;
    }

    if (accountListMenuId === 'all') {
      setSelectedView(defaultFilterValue);
      setToggleAdvanceFilters(false);
      setSelectedFilters({ ...initialFilterValue, accountVisibility: true, licenceStatus: ['Active'] });
    } else if (accountListMenuId && customFilterViewList?.length > 0) {
      const data = findSelectedView(accountListMenuId);

      if (!data) {
        navigate('/account-list/all');
        return;
      }

      setSelectedView(accountListMenuId);
      setToggleAdvanceFilters(data.filter?.toggleAdvanceFilter);
      setSelectedFilters(data.filter);
    }
  }, [accountListMenuId, customFilterViewList]);

  // State setter functions ----->
  const setAccountListData = () => {
    const { accountsList, loading } = accountListData;
    const localAccountList = accountsList ?? [];
    setAccountList({ ...accountList, data: localAccountList, loading });
  };

  const setFeatureData = () => {
    const { response, loading } = featureData;
    setFeatureList({ ...featureList, data: response?.responseArray, loading });
  };

  const setFeatureUsageAccountData = () => {
    const { data, loading, error } = featureUsageAccountData;
    setFeatureUsageAccounts({
      ...featureUsageAccounts,
      data,
      loading,
      error,
    });
  };

  useEffect(() => {
    setAccountListData();
  }, [accountListData.accountsList]);

  useEffect(() => {
    setFeatureData();
  }, [featureData]);

  useEffect(() => {
    setFeatureUsageAccountData();
  }, [featureUsageAccountData]);

  return {
    accountList,
    filteredAccounts,
    featureList,
    selectedFilters,
    setSelectedFilters,
    resetFilters,
    toggleAdvanceFilters,
    setToggleAdvanceFilters,
    findSelectedView,
    openDialog,
    setOpenDialog,
    selectedView,
    setSelectedView,
  };
};

export default useAccountList;
